<template>
  <v-data-table
    :headers="computedHeaders"
    :items="resultadosConFideicomisos"
    :server-items-length="totalItems"
    :items-per-page="itemsPorPagina"
    :page="paginaActual"
    :loading="loading"
    :options.sync="options"
    :item-class="getRowClass"
    :footer-props="footerProps"
    class="elevation-1"
    dense
    disable-sort
    @update:options="onOptionsUpdate"
  >
    <!-- Template para filas de encabezado de Fideicomiso -->
    <template v-slot:item="{ item }">
      <tr v-if="item._esFideicomisoHeader" class="fideicomiso-header-row">
        <td :colspan="computedHeaders.length" class="fideicomiso-header-cell">
          <strong>{{ item._nombreFideicomiso }}</strong>
        </td>
      </tr>
      <tr v-else>
        <td v-for="header in computedHeaders" :key="header.value">
          <slot :name="`item.${header.value}`" :item="item">
            {{ item[header.value] }}
          </slot>
        </td>
      </tr>
    </template>

    <!-- Templates base comunes -->
    <template v-slot:item.legajo="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        <a
          v-if="item.legajo"
          :href="getGestionarUrl(item.legajo)"
          target="_blank"
          class="legajo-link"
        >
          {{ item.legajo }}
        </a>
        <template v-else>-</template>
      </template>
    </template>
    <template v-slot:item.fideicomiso="{ item }">
      <template v-if="item._esSubtotal && esOperacion === 'Info Cte y No Cte'">
        <strong class="subtotal-label">📈 {{ item._cantidadRegistros }} registros</strong>
      </template>
      <template v-else>
        {{ item.fideicomisoName || '-' }}
      </template>
    </template>

    <template v-slot:item.operatoria="{ item }">
      <template v-if="item._esSubtotal && esOperacion === 'Info Cte y No Cte'">
        <strong class="subtotal-label">Subtotal {{ item._subtotales.operatoria || item.operatoria }}</strong>
      </template>
      <template v-else>
        {{ item.operatoria || '-' }}
      </template>
    </template>

    <template v-slot:item.cliente="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        {{
          (item.cliente && item.cliente.razonSocial && item.cliente.razonSocial.toUpperCase()) ||
          (item.cliente && item.cliente.apellido && item.cliente.nombre && (item.cliente.apellido + ' ' + item.cliente.nombre).trim().toUpperCase()) ||
          (item.nombre_razon_social && item.nombre_razon_social.toUpperCase()) ||
          '-'
        }}

      </template>
    </template>

    <template v-slot:item.cuit="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        {{ (item.cliente && item.cliente.cuit) || item.cuit || '-' }}
      </template>
    </template>

    <template v-slot:item.departamento="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        <span>{{ getDepartamentoNombre(item) }}</span>
      </template>
    </template>

    <template v-slot:item.persona="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        <v-chip
          small
          :color="(item.cliente && item.cliente.tipoCliente === 1) || item.persona === 'Física' ? 'blue' : 'purple'"
          text-color="white"
        >
          {{
            (item.cliente && (item.cliente.tipoCliente === 0 ? 'Jurídica' : 'Física')) ||
            item.persona ||
            '-'
          }}
        </v-chip>
      </template>
    </template>

    <template v-slot:item.estado="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        <v-chip small :color="obtenerColorEstado(item.estado)" text-color="white">
          {{ item.estado || '-' }}
        </v-chip>
      </template>
    </template>

    <template v-slot:item.fechaOtorgamiento="{ item }">
      <div v-if="item.fechaOtorgamiento" class="desembolso-column">
        <div class="desembolso-monto">
          {{ item.fechaOtorgamiento }}
        </div>
      </div>
      <div v-else class="text-center">-</div>
    </template>

    <template v-slot:item.fecha_ingreso="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        {{ formatDates(item.fechaIngreso) }}
      </template>
    </template>

    <template v-slot:item.fechaIngreso="{ item }">
      <template v-if="!(item._esSubtotal && esOperacion === 'Info Cte y No Cte')">
        {{ formatDates(item.fechaIngreso) }}
      </template>
    </template>

    <template v-slot:item.monto="{ item }">
      <div class="text-center">${{ formatoNumero(item.monto || 0) }}</div>
    </template>

    <!-- Template para monto otorgado (Info Cte y No Cte) -->
    <template v-slot:item.monto_otorgado="{ item }">
      <template v-if="item._esSubtotal && esOperacion === 'Info Cte y No Cte'">
        <strong class="subtotal-value">${{ formatoNumero(item._subtotales.monto_otorgado || 0) }}</strong>
      </template>
      <template v-else>
        <div class="text-center">${{ formatoNumero(item.monto_otorgado || 0) }}</div>
      </template>
    </template>

    <!-- Template para capital corriente -->
    <template v-slot:item.capital_corriente="{ item }">
      <template v-if="item._esSubtotal && esOperacion === 'Info Cte y No Cte'">
        <strong class="subtotal-value">${{ formatoNumero(item._subtotales.capital_corriente || 0) }}</strong>
      </template>
      <template v-else>
        <div class="text-center">${{ formatoNumero(item.capital_corriente || 0) }}</div>
      </template>
    </template>

    <!-- Template para capital no corriente -->
    <template v-slot:item.capital_no_corriente="{ item }">
      <template v-if="item._esSubtotal && esOperacion === 'Info Cte y No Cte'">
        <strong class="subtotal-value">${{ formatoNumero(item._subtotales.capital_no_corriente || 0) }}</strong>
      </template>
      <template v-else>
        <div class="text-center">${{ formatoNumero(item.capital_no_corriente || 0) }}</div>
      </template>
    </template>

    <!-- Template para saldo capital total -->
    <template v-slot:item.saldo_capital_total="{ item }">
      <template v-if="item._esSubtotal && esOperacion === 'Info Cte y No Cte'">
        <strong class="subtotal-value">${{ formatoNumero(item._subtotales.saldo_capital_total || 0) }}</strong>
      </template>
      <template v-else>
        <div class="text-center">${{ formatoNumero(item.saldo_capital_total || 0) }}</div>
      </template>
    </template>

    <!-- Template para monto total solicitado -->
    <template v-slot:item.monto_total_solicitado="{ item }">
      <div class="text-center">
        ${{ formatoNumero(getTipoMonto(item.parametros, 'monto')) }}
      </div>
    </template>

    <!-- Templates para gastos con nombres específicos -->
    <template v-slot:item.honorarios_mf="{ item }">
      <div v-if="getGastoAdministrativo(item) > 0" class="text-center">
        ${{ formatoNumero(getGastoAdministrativo(item)) }}
      </div>
      <div v-else class="text-center">-</div>
    </template>

    <template v-slot:item.gastos_escribano="{ item }">
      <div v-if="getGastoOtorgamiento(item) > 0" class="text-center">
        ${{ formatoNumero(getGastoOtorgamiento(item)) }}
      </div>
      <div v-else class="text-center">-</div>
    </template>

    <template v-slot:item.otros_gastos="{ item }">
      <div v-if="getGastoIibb(item) > 0" class="text-center">
        ${{ formatoNumero(getGastoIibb(item)) }}
      </div>
      <div v-else class="text-center">-</div>
    </template>

    <!-- Slots personalizables para cada tipo de tabla -->
    <template
      v-for="slot in Object.keys($scopedSlots)"
      :slot="slot"
      slot-scope="scope"
    >
      <slot :name="slot" v-bind="scope" />
    </template>

    <template v-slot:no-data>
      <div class="text-center pa-4">
        <p>No se encontraron datos</p>
      </div>
    </template>

    <template v-slot:body.append>
      <tr
        style="background-color: #f5f7fa; font-weight: bold"
        class="totals-row"
      >
        <td></td>
        <td>
          <strong class="totals-text">TOTAL - RESUMEN : </strong>
          <strong class="totals-text">{{ resultados.length }}</strong>
        </td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <!-- Slots personalizables para totales -->
        <template v-if="esOperacion === 'Info Cte y No Cte'">
          <td class="text-center">
            <strong class="totals-text">${{ formatoNumero(calculatedTotals.totalMontoOtorgado) }}</strong>
          </td>
          <td class="text-center">
            <strong class="totals-text">${{ formatoNumero(calculatedTotals.totalSaldoCapitalTotal) }}</strong>
          </td>
          <td class="text-center">
            <strong class="totals-text">${{ formatoNumero(calculatedTotals.totalCapitalCorriente) }}</strong>
          </td>
          <td class="text-center">
            <strong class="totals-text">${{ formatoNumero(calculatedTotals.totalCapitalNoCorriente) }}</strong>
          </td>
        </template>
        <template v-else>
          <td></td>
          <td></td>
          <slot name="totals-row" :totals="calculatedTotals" />
        </template>
      </tr>
    </template>
  </v-data-table>
</template>

<script>
import { formatDateUnix } from '../../../common/util';
import ReportCalculations from '../ReportCalculations';

export default {
  name: 'BaseReportTable',
  mixins: [ReportCalculations],
  computed: {
    resultadosConFideicomisos() {
      return this.agruparPorFideicomiso(this.resultados);
    },
    computedHeaders() {
      return this.headers;
    },
    calculatedTotals() {
      return {
        totalMonto: this.totalMonto,
        totalMontoAprobado: this.totalMontoAprobado,
        totalMontoPendiente: this.totalMontoPendiente || 0,
        totalGastoAdministrativo: this.totalGastoAdministrativo,
        totalGastoIibb: this.totalGastoIibb,
        totalGastoOtorgamiento: this.totalGastoOtorgamiento,
        totalMontoLiquidado: this.totalMontoLiquidado || 0,
        // Totales específicos para Info Cte y No Cte
        totalMontoOtorgado: this.totalMontoOtorgado || 0,
        totalSaldoCapitalTotal: this.totalSaldoCapitalTotal || 0,
        totalCapitalCorriente: this.totalCapitalCorriente || 0,
        totalCapitalNoCorriente: this.totalCapitalNoCorriente || 0,
      };
    },
    footerProps() {
      // Configuración específica para Info Cobranzas
      if (this.esOperacion === 'Info Cobranzas') {
        return {
          'items-per-page-options': [5, 10, 15, 50, 100]
        };
      }
      // Configuración por defecto para otros reportes
      return {
        'items-per-page-options': [10, 25, 50, 100]
      };
    }
  },
  props: {
    resultados: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPorPagina: {
      type: Number,
      default: 10,
    },
    paginaActual: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    esOperacion: {
      type: String,
    },
    itemClass: {
      type: Function,
      default: null,
    },
  },
  emits: ['cambiar-pagina', 'cambiar-items-por-pagina'],
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
      },
    };
  },
  methods: {
    getGestionarUrl(legajo) {
      const baseUrl = process.env.VUE_APP_GESTIONAR_URL || 'http://localhost:8080';
      return `${baseUrl}/gestionar/${legajo}`;
    },
    getDepartamentoNombre(item) {
      // Si el item ya tiene el campo departamento mapeado, usarlo
      if (item.departamento) {
        return item.departamento;
      }

      // Intentar obtener departamento desde diferentes estructuras
      if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
        return item.localidad.departamento.nombre;
      }
      if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
        return item.cliente.localidad.departamento.nombre;
      }

      // Si no hay departamento, retornar "-"
      return '-';
    },
    getRowClass(item) {
      if (this.itemClass) {
        return this.itemClass(item);
      }
      // Para Info Cte y No Cte, aplicar clase de subtotales
      if (item._esSubtotal && this.esOperacion === 'Info Cte y No Cte') {
        return 'subtotal-row';
      }
      return '';
    },
    obtenerColorEstado(estado) {
      const colores = {
        Otorgado: 'green',
        Aprobado: 'blue',
        Análisis: 'orange',
        Rechazado: 'red',
        Cancelado: 'grey',
        Desistido: 'brown',
      };
      return colores[estado] || 'grey';
    },
    formatDates(utc) {
      return utc ? formatDateUnix(utc) : '';
    },
    onOptionsUpdate(newOptions) {
      if (newOptions.page !== this.paginaActual) {
        this.$emit('cambiar-pagina', newOptions.page);
      }
      if (newOptions.itemsPerPage !== this.itemsPorPagina) {
        this.$emit('cambiar-items-por-pagina', newOptions.itemsPerPage);
      }
    },
    getGastoAdministrativo(item) {
      const gastoAdmin =
        item.gastos && item.gastos.administrativo
          ? parseFloat(item.gastos.administrativo)
          : 0;
      const honorariosMf =
        item.gastos && item.gastos.honorarios_mf
          ? parseFloat(item.gastos.honorarios_mf)
          : 0;
      return gastoAdmin + honorariosMf;
    },
    getGastoIibb(item) {
      const gastoIibb =
        item.gastos && item.gastos.iibb ? parseFloat(item.gastos.iibb) : 0;
      const otrosGastos =
        item.gastos && item.gastos.otros_gastos
          ? parseFloat(item.gastos.otros_gastos)
          : 0;
      return gastoIibb + otrosGastos;
    },
    getGastoOtorgamiento(item) {
      const gastoOtorg =
        item.gastos && item.gastos.otorgamiento
          ? parseFloat(item.gastos.otorgamiento)
          : 0;
      const gastosEscribano =
        item.gastos && item.gastos.gastos_escribano
          ? parseFloat(item.gastos.gastos_escribano)
          : 0;
      return gastoOtorg + gastosEscribano;
    },
  },
  watch: {
    paginaActual(newVal) {
      this.options.page = newVal;
    },
    itemsPorPagina(newVal) {
      this.options.itemsPerPage = newVal;
    },
  },
};
</script>

<style scoped>
.v-data-table {
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
  border-radius: 8px;
}

.v-data-table >>> .v-data-table-header {
  background-color: #949faf !important;
}

.v-data-table >>> .v-data-table-header th {
  background-color: #949faf !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
  color: #000000 !important;
  text-transform: uppercase !important;
  padding: 6px 8px !important;
  border-bottom: 1px solid #e0e0e0 !important;
}

.v-data-table >>> thead tr th {
  background-color: #949faf !important;
  font-weight: 700 !important;
  color: #000000 !important;
}

td {
  padding: 6px 8px !important;
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
  color: #424242;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.v-data-footer {
  background-color: #fafafa;
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
}

.v-data-table tbody tr:hover {
  background-color: #f0f4f8;
}

.v-card-actions,
tfoot {
  background-color: #f5f7fa;
  padding: 12px;
}

tfoot td {
  font-weight: bold;
  color: #1e1e1e;
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
}

.desembolso-column {
  padding: 2px;
  text-align: center;
  font-family: 'Roboto', sans-serif !important;
}

.desembolso-monto {
  font-weight: bold;
  color: #28a745;
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
  margin-bottom: 2px;
}

.desembolso-estado {
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
  color: #6c757d;
  font-style: italic;
  margin-bottom: 2px;
}

.desembolso-fecha {
  font-size: 11px !important;
  font-family: 'Roboto', sans-serif !important;
  color: #6c757d;
  text-align: center;
}

.estado-rechazado {
  color: #dc3545 !important;
  font-weight: bold !important;
}

/* Estilos para la fila de totales - letra más pequeña */
.totals-row .totals-text {
  font-size: 12px !important;
  font-family: 'Roboto', sans-serif !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.totals-row strong {
  font-size: 12px !important;
  font-family: 'Roboto', sans-serif !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

/* Aplicar estilos a TODOS los elementos strong en filas de totales */
.v-data-table tbody tr:last-child strong {
  font-size: 12px !important;
  font-family: 'Roboto', sans-serif !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

/* También aplicar a las celdas de totales para mayor especificidad */
.v-data-table tbody tr:last-child td {
  white-space: nowrap !important;
}

/* Estilos con máxima especificidad para forzar el comportamiento */
.v-data-table >>> tbody tr:last-child strong,
.v-data-table >>> .totals-row strong,
.v-data-table >>> .totals-row .totals-text {
  font-size: 12px !important;
  white-space: nowrap !important;
  display: inline !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}

.v-data-table >>> tbody tr:last-child td,
.v-data-table >>> .totals-row td {
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}

.totals-row td {
  white-space: nowrap !important;
  min-width: 120px !important;
  max-width: 150px !important;
}

/* Estilos para subtotales también - letra más pequeña */
.subtotal-value {
  font-size: 12px !important;
  font-family: 'Roboto', sans-serif !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  font-weight: 600;
  color: #28a745;
}

/* Estilos para filas de subtotales de Info Cte y No Cte */
.subtotal-row {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-top: 2px solid #007bff !important;
  border-bottom: 2px solid #007bff !important;
  border-left: 4px solid #007bff !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 4px rgba(0,123,255,0.1) !important;
}

.subtotal-row:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,123,255,0.15) !important;
  transition: all 0.2s ease-in-out;
}

.subtotal-label {
  font-weight: 700;
  color: #007bff;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.legajo-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.legajo-link:hover {
  color: #1565c0;
  text-decoration: underline;
}

/* Estilos para filas de encabezado de fideicomiso */
.fideicomiso-header-row {
  background-color: #f5f5f5 !important;
}

.fideicomiso-header-cell {
  background-color: #f5f5f5 !important;
  padding: 12px 16px !important;
  font-weight: 600;
  font-size: 12px !important;
  color: #424242;
  border-bottom: 2px solid #e0e0e0 !important;
}
</style>
